import React, { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"

import { Box, Grid } from "@mui/material"
import { InputText, Snackbar, Tab, Tabs, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"
import { useFormikContext } from "formik"

import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { KonanRegistryImagesSeriliazer } from "../../../types/generated/api/KonanRegistryImagesSeriliazer"
import type { ModelCreationFormProps, ModelCreationFormikValues } from "../interfaces"

import styles from "../Projects.module.scss"

/**
 * Model creation form
 * @param  {string} errorMessage creation error messages
 * @return  {React.ReactElement}
 */
export function ModelCreationForm(props: Readonly<ModelCreationFormProps>): React.ReactElement {
  const { errorMessage } = props
  const { t } = useTranslation("projects")

  const { values, errors, touched, isSubmitting, setFieldValue, handleChange, handleBlur } =
    useFormikContext<ModelCreationFormikValues>()

  // Fetching container images
  const orgId = useSelector((state: RootState) => state.auth.organizationId)

  const fetchRegistryImages = useQuery<Array<KonanRegistryImagesSeriliazer>, AxiosError>(
    ["registry-images", orgId],
    () => KonanAPI.fetchRegistryImages(),
  )

  // Memoizing the image list to prevent unnecessary re-renders
  const imagesList = useMemo(() => {
    const images: string[] = []

    fetchRegistryImages?.data?.forEach((item) => images.push(item.image))

    return images ?? undefined
  }, [fetchRegistryImages?.data])

  return (
    <Grid container item direction="column" className={styles.imageContainer}>
      {errorMessage && (
        <Grid item paddingBottom={2}>
          <Snackbar severity="error" fullWidth description={errorMessage} />
        </Grid>
      )}

      <Typography variant="h3-bold" gutterBottom>
        {t("creationFlow.modelForm.model")}
      </Typography>
      <InputText
        label={t("creationFlow.fields.name")}
        id="name"
        placeholder={t("creationFlow.modelForm.initialModelPlaceholder")}
        value={values.name}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={touched.name && Boolean(errors.name) && errors.name}
        fullWidth
        required
        hideDescription
      />

      <Grid item paddingTop={2}>
        <Typography variant="h3-bold" gutterBottom>
          {t("creationFlow.modelForm.containerImage")}
        </Typography>

        <Typography variant="label-bold">{t("creationFlow.modelForm.chooseMethod")}</Typography>

        <Tabs
          value={values.registry}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={(e: any) => {
            setFieldValue("registry", e.target.value)
          }}
          className={"base-tabs"}
        >
          <Tab
            label={t("creationFlow.modelForm.kcr")}
            value="KCR"
            selected={"KCR" === values.registry ? true : false}
            // To reset container image shifting from one mode to another
            onClick={() => setFieldValue("image_url", "")}
            size="large"
          />
          <Tab
            label={t("creationFlow.modelForm.custom")}
            value="Custom"
            selected={"Custom" === values.registry ? true : false}
            // To reset container image shifting from one mode to another
            onClick={() => setFieldValue("image_url", "")}
            size="large"
          />
        </Tabs>
      </Grid>

      <Grid item className={styles.modelCreationContainer}>
        <Grid container item spacing={2}>
          <Grid item xs={12} mt={2}>
            {values.registry === "KCR" ? (
              <InputText
                id="image_url"
                label={t("creationFlow.modelForm.containerImageUrl")}
                placeholder={t("creationFlow.modelForm.kcrPlaceholder")}
                value={values.image_url}
                // add key prop when using Autocomplete to identify which items in the list are changed, updated, or deleted
                key={imagesList ? imagesList[0] : ""}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={touched.image_url && Boolean(errors.image_url) && errors.image_url}
                disabled={isSubmitting}
                // TODO:: fix this
                // description={
                //   <React.Fragment>
                //     Instructions can be found{" "}
                //     <LinkRouter color="textPrimary" to="/kcr">
                //       here
                //     </LinkRouter>{" "}
                //     on how to push your container images to KCR
                //   </React.Fragment>
                // }
                fullWidth
                required
                options={imagesList}
                hideDescription
              />
            ) : (
              <InputText
                id="image_url"
                label={t("creationFlow.modelForm.containerImageUrl")}
                placeholder={t("creationFlow.modelForm.customImagePlaceholder")}
                value={values.image_url}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={touched.image_url && Boolean(errors.image_url) && errors.image_url}
                description={t("creationFlow.modelForm.imageUrlDescription")}
                fullWidth
                required
              />
            )}
            <Box mb={2} />

            <InputText
              id="exposed_port"
              label={t("creationFlow.modelForm.port")}
              value={values.exposed_port.toString()}
              handleChange={handleChange}
              handleBlur={handleBlur}
              error={touched.exposed_port && Boolean(errors.exposed_port) && errors.exposed_port}
              fullWidth
              required
              hideDescription
            />

            {values.registry !== "KCR" && (
              <Grid container item xs={12} spacing={2} mb={2} mt={0}>
                <Grid item xs={6}>
                  <InputText
                    id="docker_username"
                    placeholder={t("creationFlow.modelForm.registryUsernamePlaceholder")}
                    label={t("creationFlow.modelForm.registryUsernameOptional")}
                    value={values.docker_username}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.docker_username && Boolean(errors.docker_username) && errors.docker_username}
                    description={t("creationFlow.modelForm.authenticationDescription")}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={6}>
                  <InputText
                    id="docker_password"
                    label={t("creationFlow.modelForm.registryPasswordOptional")}
                    placeholder={t("creationFlow.modelForm.registryPasswordPlaceholder")}
                    type="password"
                    value={values.docker_password}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.docker_password && Boolean(errors.docker_password) && errors.docker_password}
                    description={t("creationFlow.modelForm.authenticationDescription")}
                    fullWidth
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
